/**
 * Entitlement helpers shared by `AuthGuard` and `useEntitlement`.
 *
 * Centralizes the normalization + display-format rules so the gating runtime
 * and the restricted-fallback copy stay consistent.
 */

/**
 * Render a list of required entitlements as a human-readable string for
 * restricted-fallback copy. Uses Oxford-comma formatting for 3+ items.
 *
 * @example
 *   formatEntitlements("ops")                → "ops"
 *   formatEntitlements(["ops"])              → "ops"
 *   formatEntitlements(["ops", "crm"])       → "ops or crm"
 *   formatEntitlements(["ops", "crm", "ai"]) → "ops, crm, or ai"
 *   formatEntitlements([])                   → ""
 */
export function formatEntitlements(entitlement: string | string[]): string {
  const list = Array.isArray(entitlement) ? entitlement : [entitlement];
  if (list.length === 0) return "";
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} or ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, or ${list[list.length - 1]}`;
}

/**
 * Normalize the `entitlement` prop to an array of required keys, or `null`
 * when no gate applies (`undefined` or empty array).
 *
 * AuthGuard semantics: `null` ≡ "no entitlement gate" (authenticated user
 * always passes). useEntitlement has different empty-array semantics —
 * see the hook for details.
 */
export function normalizeEntitlement(entitlement: string | string[] | undefined): string[] | null {
  if (entitlement === undefined) return null;
  const list = Array.isArray(entitlement) ? entitlement : [entitlement];
  return list.length === 0 ? null : list;
}
